/**
 * Estimates Module (TypeScript)
 * Handles estimate editing, caching, and synchronization.
 */

import { roundToTwo, parseCurrencyValue } from './formatting';
import { syncEstimateRemote } from './api-operations';
import { getSellChannels } from './sellChannels';
import { renderCompsPanel } from './compsPanel';
import { showToast } from './ui.js';
import type { State, Lot, EstimateOverride } from '../types';
import { nowIso } from '@lotlingo/shared';
import { createLogger } from '../../shared/logger.js';
import { updateEstimateCellInPlace } from './polling.js';

const log = createLogger('portal:estimates');

// =============================
// ESTIMATE CACHE
// =============================

const estimateCache = new Map<string, string>();

export function setCachedEstimate(lotId: string, estimate: string): void {
  estimateCache.set(lotId, estimate);
}

export function getCachedEstimate(lotId: string): string | undefined {
  return estimateCache.get(lotId);
}

// =============================
// ESTIMATE INPUT HELPERS
// =============================

function formatEstimateInputValue(value?: number, raw?: string | number | null): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return roundToTwo(value).toFixed(2);
  }

  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return roundToTwo(raw).toFixed(2);
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) {
      return '';
    }
    if (/^[0-9]+(?:\.[0-9]{1,2})?$/.test(trimmed)) {
      return trimmed;
    }
    const parsedFromRaw = parseCurrencyValue(trimmed);
    if (parsedFromRaw != null) {
      return roundToTwo(parsedFromRaw).toFixed(2);
    }
    return '';
  }

  const parsed = parseCurrencyValue(raw);
  if (parsed != null) {
    return roundToTwo(parsed).toFixed(2);
  }

  return '';
}

function sanitizeEstimateInputString(str?: string | null): string {
  return typeof str === 'string' ? str.trim() : '';
}

function parseEstimateInputValue(str?: string | null): number | null {
  if (!str || str.trim().length === 0) {
    return null;
  }
  const num = Number(str);
  return Number.isFinite(num) ? roundToTwo(num) : null;
}

// =============================
// PRELOAD ESTIMATES
// =============================

export function preloadEstimatesAfterLogin(lots: Lot[]): void {
  if (!Array.isArray(lots)) return;
  lots.forEach(lot => {
    if (!lot || !lot.id) return;
    const hasLowEstimate = lot.lowSellValue != null && Number.isFinite(Number(lot.lowSellValue));
    const hasHighEstimate = lot.highSellValue != null && Number.isFinite(Number(lot.highSellValue));
    if (hasLowEstimate || hasHighEstimate) {
      const lowSell = hasLowEstimate ? roundToTwo(Number(lot.lowSellValue)) : null;
      const highSell = hasHighEstimate ? roundToTwo(Number(lot.highSellValue)) : null;
      const lowDisplay = lowSell != null ? '$' + lowSell.toFixed(2) : '—';
      const highDisplay = highSell != null ? '$' + highSell.toFixed(2) : '—';
      const lowHtml = lowSell != null ? '<strong>' + lowDisplay + '</strong>' : lowDisplay;
      setCachedEstimate(lot.id, lowHtml + ' - ' + highDisplay);
    }
  });
}

// =============================
// ESTIMATE EDITING
// =============================

// (Previous determineEstimateSource helper removed; logic now inlined in beginEstimateEdit)

export function beginEstimateEdit(lot: Lot, state: State): void {
  if (!lot || !lot.id) {
    return;
  }
  if (state.pendingEstimateIds && state.pendingEstimateIds.has(lot.id)) {
    return;
  }

  const isMobile = window.innerWidth <= 768;

  const estimateModal = document.getElementById('estimate-modal');
  const estimateBackdrop = document.getElementById('estimate-modal-backdrop');
  const lowInput = document.getElementById('estimate-low-value');
  const highInput = document.getElementById('estimate-high-value');
  const biddingToggle = document.getElementById('estimate-bidding-toggle');
  const bidAmountInput = document.getElementById('estimate-bid-amount');
  const bidAmountField = document.getElementById('estimate-bid-amount-field');
  const userMaxBidInput = document.getElementById('estimate-user-max-bid');
  const premiumOverrideInput = document.getElementById('estimate-premium-override');
  const sellChannelSelect = document.getElementById('estimate-sell-channel');

  if (
    !estimateModal ||
    !estimateBackdrop ||
    !(lowInput instanceof HTMLInputElement) ||
    !(highInput instanceof HTMLInputElement) ||
    !(biddingToggle instanceof HTMLInputElement) ||
    !(bidAmountInput instanceof HTMLInputElement) ||
    !bidAmountField
  ) {
    return;
  }

  // Determine effective estimates prioritizing:
  // 1. Explicit user overrides (raw if provided else amount)
  // 2. Existing mapped lot values (lowSellValue / highSellValue)
  // 3. AI analysis fallback if still absent
  let lowRawCandidate: string | number | undefined = lot.userOverlay?.lowSellOverride?.raw;
  let lowAmountCandidate = lot.userOverlay?.lowSellOverride?.amount;
  let highRawCandidate: string | number | undefined = lot.userOverlay?.highSellOverride?.raw;
  let highAmountCandidate = lot.userOverlay?.highSellOverride?.amount;

  if (lowRawCandidate == null && lowAmountCandidate == null) {
    lowRawCandidate = lot.lowSellValueRaw;
    lowAmountCandidate = lot.lowSellValue;
  }
  if (highRawCandidate == null && highAmountCandidate == null) {
    highRawCandidate = lot.highSellValueRaw;
    highAmountCandidate = lot.highSellValue;
  }

  // Timestamp-based override: if AI completed AFTER user override, prefer AI values
  const sharedLot: any = (lot as any).sharedLot;
  const aiAnalysis = sharedLot?.aiAnalysis;
  const aiCompletedAt = typeof lot.aiCompletedAt === 'string' ? Date.parse(lot.aiCompletedAt) : NaN;
  const userUpdatedAt =
    typeof lot.userOverlay?.updatedAt === 'string' ? Date.parse(lot.userOverlay!.updatedAt!) : NaN;
  const aiIsNewer =
    Number.isFinite(aiCompletedAt) &&
    Number.isFinite(userUpdatedAt) &&
    aiCompletedAt > userUpdatedAt;
  if (aiIsNewer && aiAnalysis && typeof aiAnalysis === 'object') {
    // Only replace if current candidates are still empty
    if (
      lowRawCandidate == null &&
      lowAmountCandidate == null &&
      aiAnalysis.lowPrice?.amount != null
    ) {
      lowAmountCandidate = aiAnalysis.lowPrice.amount;
      lowRawCandidate = aiAnalysis.lowPrice.raw ?? aiAnalysis.lowPrice.amount;
    }
    if (
      highRawCandidate == null &&
      highAmountCandidate == null &&
      aiAnalysis.highPrice?.amount != null
    ) {
      highAmountCandidate = aiAnalysis.highPrice.amount;
      highRawCandidate = aiAnalysis.highPrice.raw ?? aiAnalysis.highPrice.amount;
    }
  }

  state.estimateEdit = {
    id: lot.id,
    low: String(
      formatEstimateInputValue(
        typeof lowAmountCandidate === 'number' ? lowAmountCandidate : undefined,
        lowRawCandidate as any
      )
    ),
    high: String(
      formatEstimateInputValue(
        typeof highAmountCandidate === 'number' ? highAmountCandidate : undefined,
        highRawCandidate as any
      )
    ),
    bidding: !!lot.bidding,
    bidAmount: lot.bidAmount != null ? lot.bidAmount.toFixed(2) : '',
    userMaxBid:
      typeof lot.userMaxBid === 'number' && Number.isFinite(lot.userMaxBid)
        ? lot.userMaxBid.toFixed(2)
        : '',
    premiumOverride:
      lot.feeOverrides &&
      typeof lot.feeOverrides.buyerPremiumPct === 'number' &&
      Number.isFinite(lot.feeOverrides.buyerPremiumPct)
        ? String(lot.feeOverrides.buyerPremiumPct)
        : '',
    sellChannel: typeof lot.sellChannel === 'string' ? lot.sellChannel : '',
  } as {
    id: string;
    low?: string;
    high?: string;
    bidding?: boolean;
    bidAmount?: string;
    userMaxBid?: string;
    premiumOverride?: string;
    sellChannel?: string;
  };

  lowInput.value = state.estimateEdit.low || '';
  highInput.value = state.estimateEdit.high || '';
  biddingToggle.checked = state.estimateEdit.bidding || false;
  bidAmountInput.value = state.estimateEdit.bidAmount || '';
  bidAmountField.hidden = !state.estimateEdit.bidding;

  if (userMaxBidInput instanceof HTMLInputElement) {
    userMaxBidInput.value = state.estimateEdit.userMaxBid || '';
  }
  if (premiumOverrideInput instanceof HTMLInputElement) {
    premiumOverrideInput.value = state.estimateEdit.premiumOverride || '';
    // Surface the effective fallback (scraped premium, else user default) as placeholder
    const scraped =
      typeof lot.buyerPremiumPct === 'number' && Number.isFinite(lot.buyerPremiumPct)
        ? lot.buyerPremiumPct
        : null;
    premiumOverrideInput.placeholder = scraped != null ? `${scraped} (from listing)` : 'Default';
  }

  if (sellChannelSelect instanceof HTMLSelectElement) {
    // (Re)populate options from presets, keeping the default first option
    const channels = getSellChannels(state.settings);
    while (sellChannelSelect.options.length > 1) {
      sellChannelSelect.remove(1);
    }
    for (const channel of channels) {
      const option = document.createElement('option');
      option.value = channel.id;
      option.textContent = `${channel.label} (${channel.sellerCostsPct}%)`;
      sellChannelSelect.add(option);
    }
    sellChannelSelect.value = state.estimateEdit.sellChannel || '';
    if (sellChannelSelect.value !== (state.estimateEdit.sellChannel || '')) {
      // Stored channel no longer exists in presets; fall back to default
      sellChannelSelect.value = '';
    }
  }

  // "Why this price?" — sold comps that informed the AI estimate
  renderCompsPanel(document.getElementById('estimate-comps-panel'), lot);

  estimateModal.removeAttribute('hidden');
  estimateBackdrop.removeAttribute('hidden');
  document.body?.classList.add('modal-open');

  window.requestAnimationFrame(() => {
    lowInput.focus();
    lowInput.select();
  });
}

export function cancelEstimateEdit(state: State, render: () => void): void {
  if (!state.estimateEdit) {
    return;
  }
  state.estimateEdit = null;
  render();
}

export function commitEstimateEditFromModal(
  state: State,
  commitEstimateEditFn: () => void,
  closeEstimateModal: () => void
): void {
  if (!state.estimateEdit || !state.estimateEdit.id) {
    closeEstimateModal();
    return;
  }

  const lowInput = document.getElementById('estimate-low-value');
  const highInput = document.getElementById('estimate-high-value');
  const biddingToggle = document.getElementById('estimate-bidding-toggle');
  const bidAmountInput = document.getElementById('estimate-bid-amount');
  const userMaxBidInput = document.getElementById('estimate-user-max-bid');
  const premiumOverrideInput = document.getElementById('estimate-premium-override');
  const sellChannelSelect = document.getElementById('estimate-sell-channel');

  if (lowInput instanceof HTMLInputElement && highInput instanceof HTMLInputElement) {
    state.estimateEdit.low = lowInput.value;
    state.estimateEdit.high = highInput.value;
  }

  if (biddingToggle instanceof HTMLInputElement) {
    state.estimateEdit.bidding = biddingToggle.checked;
  }

  if (bidAmountInput instanceof HTMLInputElement) {
    state.estimateEdit.bidAmount = bidAmountInput.value;
  }

  if (userMaxBidInput instanceof HTMLInputElement) {
    state.estimateEdit.userMaxBid = userMaxBidInput.value;
  }

  if (premiumOverrideInput instanceof HTMLInputElement) {
    state.estimateEdit.premiumOverride = premiumOverrideInput.value;
  }

  if (sellChannelSelect instanceof HTMLSelectElement) {
    state.estimateEdit.sellChannel = sellChannelSelect.value;
  }

  commitEstimateEditFn();
  closeEstimateModal();
}

type OverlayOverrideSnapshot = EstimateOverride | null | '__absent__' | undefined;

export function commitEstimateEdit(
  state: State,
  render: () => void,
  save: () => void,
  applyRemoteSnapshot: (lots: Lot[]) => void,
  applyModeBanner: () => void,
  ensureAccessToken: (retry?: boolean) => Promise<string | null>,
  mapRemoteDocument: (data: any) => Lot | null | undefined,
  DEBUG: boolean
): void {
  if (!state.estimateEdit || !state.estimateEdit.id) {
    return;
  }
  const lotId = state.estimateEdit.id;
  const lot = state.lots.find(item => item && item.id === lotId);
  if (!lot) {
    state.estimateEdit = null;
    render();
    return;
  }

  const sanitizedLow = sanitizeEstimateInputString(state.estimateEdit.low || '');
  const sanitizedHigh = sanitizeEstimateInputString(state.estimateEdit.high || '');
  state.estimateEdit.low = sanitizedLow;
  state.estimateEdit.high = sanitizedHigh;

  const newLowValue = parseEstimateInputValue(sanitizedLow);
  const newHighValue = parseEstimateInputValue(sanitizedHigh);
  const newBidding = !!state.estimateEdit.bidding;
  const newBidAmount = parseEstimateInputValue(state.estimateEdit.bidAmount || '');
  const newUserMaxBid = parseEstimateInputValue(state.estimateEdit.userMaxBid || '');
  const newPremiumOverride = parseEstimateInputValue(state.estimateEdit.premiumOverride || '');
  const newSellChannel = (state.estimateEdit.sellChannel || '').trim() || null;

  const currentLow =
    typeof lot.lowSellValue === 'number' && Number.isFinite(lot.lowSellValue)
      ? roundToTwo(lot.lowSellValue)
      : null;
  const currentHigh =
    typeof lot.highSellValue === 'number' && Number.isFinite(lot.highSellValue)
      ? roundToTwo(lot.highSellValue)
      : null;
  const currentBidding = !!lot.bidding;
  const currentBidAmount =
    typeof lot.bidAmount === 'number' && Number.isFinite(lot.bidAmount)
      ? roundToTwo(lot.bidAmount)
      : null;
  const currentUserMaxBid =
    typeof lot.userMaxBid === 'number' && Number.isFinite(lot.userMaxBid)
      ? roundToTwo(lot.userMaxBid)
      : null;
  const currentPremiumOverride =
    lot.feeOverrides &&
    typeof lot.feeOverrides.buyerPremiumPct === 'number' &&
    Number.isFinite(lot.feeOverrides.buyerPremiumPct)
      ? lot.feeOverrides.buyerPremiumPct
      : null;
  const currentSellChannel =
    typeof lot.sellChannel === 'string' && lot.sellChannel ? lot.sellChannel : null;

  let hasChanges =
    newLowValue !== currentLow ||
    newHighValue !== currentHigh ||
    newBidding !== currentBidding ||
    newBidAmount !== currentBidAmount ||
    newUserMaxBid !== currentUserMaxBid ||
    newPremiumOverride !== currentPremiumOverride ||
    newSellChannel !== currentSellChannel;
  if (!hasChanges) {
    if (newLowValue === null && lot.lowSellValueRaw) hasChanges = true;
    if (newHighValue === null && lot.highSellValueRaw) hasChanges = true;
    if (newLowValue !== null && lot.lowSellValueRaw) hasChanges = true;
    if (newHighValue !== null && lot.highSellValueRaw) hasChanges = true;
  }
  if (!hasChanges) {
    state.estimateEdit = null;
    render();
    return;
  }

  const cloneOverride = (value: EstimateOverride | null | undefined) => {
    if (value == null) {
      return value;
    }
    try {
      return JSON.parse(JSON.stringify(value)) as EstimateOverride;
    } catch {
      return value;
    }
  };

  const previousState: {
    lowSellValue: Lot['lowSellValue'];
    highSellValue: Lot['highSellValue'];
    lowSellValueRaw: Lot['lowSellValueRaw'];
    highSellValueRaw: Lot['highSellValueRaw'];
    bidding: Lot['bidding'];
    bidAmount: Lot['bidAmount'];
    userMaxBid: Lot['userMaxBid'];
    feeOverrides: Lot['feeOverrides'];
    sellChannel: Lot['sellChannel'];
    updatedAt: Lot['updatedAt'];
    lowSellOverride: OverlayOverrideSnapshot;
    highSellOverride: OverlayOverrideSnapshot;
    overlayUpdatedAt: string | undefined;
  } = {
    lowSellValue: lot.lowSellValue,
    highSellValue: lot.highSellValue,
    lowSellValueRaw: lot.lowSellValueRaw,
    highSellValueRaw: lot.highSellValueRaw,
    bidding: lot.bidding,
    bidAmount: lot.bidAmount,
    userMaxBid: lot.userMaxBid,
    feeOverrides: lot.feeOverrides ? { ...lot.feeOverrides } : lot.feeOverrides,
    sellChannel: lot.sellChannel,
    updatedAt: lot.updatedAt,
    lowSellOverride: lot.userOverlay
      ? Object.prototype.hasOwnProperty.call(lot.userOverlay, 'lowSellOverride')
        ? lot.userOverlay.lowSellOverride === null
          ? null
          : cloneOverride(lot.userOverlay.lowSellOverride)
        : '__absent__'
      : undefined,
    highSellOverride: lot.userOverlay
      ? Object.prototype.hasOwnProperty.call(lot.userOverlay, 'highSellOverride')
        ? lot.userOverlay.highSellOverride === null
          ? null
          : cloneOverride(lot.userOverlay.highSellOverride)
        : '__absent__'
      : undefined,
    overlayUpdatedAt:
      lot.userOverlay && typeof lot.userOverlay.updatedAt === 'string'
        ? lot.userOverlay.updatedAt
        : undefined,
  };

  const normalizedLow = newLowValue != null ? newLowValue : null;
  const normalizedHigh = newHighValue != null ? newHighValue : null;

  let nextLowOverride: EstimateOverride | null | undefined;
  if (normalizedLow === null) {
    nextLowOverride = null;
  } else if (normalizedLow != null) {
    const roundedLow = roundToTwo(normalizedLow);
    nextLowOverride = { amount: roundedLow, raw: roundedLow.toFixed(2), currency: 'USD' };
  }

  let nextHighOverride: EstimateOverride | null | undefined;
  if (normalizedHigh === null) {
    nextHighOverride = null;
  } else if (normalizedHigh != null) {
    const roundedHigh = roundToTwo(normalizedHigh);
    nextHighOverride = { amount: roundedHigh, raw: roundedHigh.toFixed(2), currency: 'USD' };
  }

  const titleForToast = lot.title ? '"' + lot.title + '"' : 'Lot';
  state.estimateEdit = null;
  lot.lowSellValue = normalizedLow ?? undefined;
  lot.highSellValue = normalizedHigh ?? undefined;
  lot.lowSellValueRaw = undefined;
  lot.highSellValueRaw = undefined;
  lot.bidding = newBidding;
  lot.bidAmount = newBidAmount ?? undefined;
  lot.userMaxBid = newUserMaxBid ?? undefined;
  if (newPremiumOverride != null) {
    lot.feeOverrides = { ...(lot.feeOverrides ?? {}), buyerPremiumPct: newPremiumOverride };
  } else if (lot.feeOverrides && lot.feeOverrides.buyerPremiumPct != null) {
    const cleared = { ...lot.feeOverrides };
    delete cleared.buyerPremiumPct;
    lot.feeOverrides = Object.keys(cleared).length ? cleared : undefined;
  }
  lot.sellChannel = newSellChannel ?? undefined;
  lot.updatedAt = Date.now();

  log.debug('commitEstimateEdit - Set lot.bidding and lot.bidAmount', {
    lotId: lot.id,
    bidding: lot.bidding,
    bidAmount: lot.bidAmount,
  });

  const hasLowEstimate = normalizedLow !== null && normalizedLow !== undefined;
  const hasHighEstimate = normalizedHigh !== null && normalizedHigh !== undefined;
  const lowDisplay = hasLowEstimate ? normalizedLow : '—';
  const highDisplay = hasHighEstimate ? normalizedHigh : '—';
  const lowHtml = hasLowEstimate ? '<strong>' + lowDisplay + '</strong>' : lowDisplay;
  setCachedEstimate(lotId, lowHtml + ' - ' + highDisplay);

  if (lot.userOverlay) {
    if (nextLowOverride === undefined) {
      delete lot.userOverlay.lowSellOverride;
    } else {
      lot.userOverlay.lowSellOverride = nextLowOverride;
    }
    if (nextHighOverride === undefined) {
      delete lot.userOverlay.highSellOverride;
    } else {
      lot.userOverlay.highSellOverride = nextHighOverride;
    }
    try {
      lot.userOverlay.updatedAt = nowIso();
    } catch {
      /* ignore */
    }
  }

  const isRemote = state.mode === 'auth0' && !!lot.remote;
  if (isRemote) {
    if (!state.pendingEstimateIds) {
      state.pendingEstimateIds = new Set();
    }
    state.pendingEstimateIds.add(lot.id);
    // Try in-place update first, fall back to full render
    if (!updateEstimateCellInPlace(lot.id, lot)) {
      render();
    }
    syncEstimateRemote(
      lot.id,
      nextLowOverride,
      nextHighOverride,
      false,
      state,
      ensureAccessToken,
      mapRemoteDocument
    )
      .then(updated => {
        if (updated) {
          const index = state.lots.findIndex(item => item && item.id === updated.id);
          if (index >= 0) {
            state.lots[index] = updated;
          } else {
            state.lots.unshift(updated);
          }
          applyRemoteSnapshot(state.lots);
          // Try in-place update first, fall back to full render
          if (!updateEstimateCellInPlace(updated.id, updated)) {
            render();
          }
          showToast('Updated estimates for ' + titleForToast + '.', { type: 'success' });
        }
      })
      .catch(err => {
        lot.lowSellValue = previousState.lowSellValue;
        lot.highSellValue = previousState.highSellValue;
        lot.lowSellValueRaw = previousState.lowSellValueRaw;
        lot.highSellValueRaw = previousState.highSellValueRaw;
        lot.bidding = previousState.bidding;
        lot.bidAmount = previousState.bidAmount;
        lot.userMaxBid = previousState.userMaxBid;
        lot.feeOverrides = previousState.feeOverrides;
        lot.sellChannel = previousState.sellChannel;
        lot.updatedAt = previousState.updatedAt;
        if (lot.userOverlay) {
          if (previousState.lowSellOverride === '__absent__') {
            delete lot.userOverlay.lowSellOverride;
          } else if (previousState.lowSellOverride !== undefined) {
            lot.userOverlay.lowSellOverride =
              previousState.lowSellOverride as EstimateOverride | null;
          }
          if (previousState.highSellOverride === '__absent__') {
            delete lot.userOverlay.highSellOverride;
          } else if (previousState.highSellOverride !== undefined) {
            lot.userOverlay.highSellOverride =
              previousState.highSellOverride as EstimateOverride | null;
          }
          if (previousState.overlayUpdatedAt) {
            lot.userOverlay.updatedAt = previousState.overlayUpdatedAt;
          }
        }
        // Try in-place update first, fall back to full render
        if (!updateEstimateCellInPlace(lot.id, lot)) {
          render();
        }
        const message = err && err.message ? err.message : String(err);
        showToast('Failed to update estimates: ' + message, { type: 'error' });
        log.error('Failed to update estimates', err);
      })
      .finally(() => {
        if (state.pendingEstimateIds) {
          state.pendingEstimateIds.delete(lot.id);
        }
        // Try in-place update first, fall back to full render
        if (!updateEstimateCellInPlace(lot.id, lot)) {
          render();
        }
        applyModeBanner();
      });
  } else {
    save();
    // Try in-place update first, fall back to full render
    if (!updateEstimateCellInPlace(lot.id, lot)) {
      render();
    }
    applyModeBanner();
    showToast('Updated estimates for ' + titleForToast + '.', { type: 'success' });
  }
}
